import { getAccessToken } from '../authentication/supabaseService';
import { getISODay, startOfWeek, isSameDay, getISOWeek, getYear, getMonth, isSameMonth, startOfISOWeek, endOfISOWeek, format } from 'date-fns';
import { RevenueSourceType } from '../../types/RevenueSourceType';

export interface RevenueChoice {
  name: string;
  quantity: number;
  price: number;
  exVat: number;
}

export interface RevenueItem {
  name: string;
  quantity: number;
  price: number;
  exVat: number;
  // Per-order choices (e.g. build-your-own wok). Only Cashdesk populates this.
  choices?: RevenueChoice[];
}

export interface RevenueCategory {
  name: string;
  quantity: number;
  price: number;
  exVat: number;
  items: RevenueItem[];
}

export interface RevenueData {
  categories: RevenueCategory[];
}

export interface RevenueHistory {
  id: string;
  messageId: string;
  gmailId: string;
  restaurantRevenueSourceId: string;
  date: Date;
  revenueData: RevenueData;
  revenueSource: string;
  revenueSourceType?: RevenueSourceType;
  restaurantId?: string;
  restaurantName?: string;
  sourceFileUrl?: string;
}

export async function fetchRevenueHistoryByRestaurant(
  restaurantId: string
): Promise<RevenueHistory[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/revenuehistory/getAll/${restaurantId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch revenue history: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return data.map((item: any) => ({
    ...item,
    date: new Date(item.date),
  }));
}

export async function fetchRevenueHistoryForAllRestaurants(): Promise<RevenueHistory[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/revenuehistory/getAllForAllRestaurants`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch revenue history for all restaurants: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return data.map((item: any) => ({
    ...item,
    date: new Date(item.date),
  }));
}

export async function fetchRevenueHistoryByRevenueSourceIds(
  revenueSourceIds: string[],
  startDate?: Date,
  endDate?: Date
): Promise<RevenueHistory[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/revenuehistory/getByRevenueSourceIds`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    // startDate/endDate are optional — when omitted the backend returns the full history.
    body: JSON.stringify({
      revenueSourceIds,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
    }),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch revenue history by revenue source IDs: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return data.map((item: any) => ({
    ...item,
    date: new Date(item.date),
  }));
}

/**
 * Fetches the earliest/latest revenue date for the given revenue sources.
 * Used by the dashboard progressive loader to know how far back to chunk.
 */
export async function fetchRevenueHistoryDateBounds(
  revenueSourceIds: string[]
): Promise<{ minDate: Date | null; maxDate: Date | null }> {
  const token = await getAccessToken();
  const res = await fetch(`/api/revenuehistory/getDateBoundsByRevenueSourceIds`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ revenueSourceIds }),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch revenue history date bounds: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    minDate: data.minDate ? new Date(data.minDate) : null,
    maxDate: data.maxDate ? new Date(data.maxDate) : null,
  };
}

export interface RevenueStats {
  todaysRevenue: number;
  thisWeekRevenue: number;
  averageWeekdayRevenue: number;
  averageWeekRevenue: number;
  monthRevenue: number;
  averageMonthRevenue: number;
  totalRevenue: number;
}

export function computeRevenueStats(revenueHistory: RevenueHistory[], showExVat: boolean = true): RevenueStats {
  const today = new Date();
  const isoToday = getISODay(today);
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });

  const todaysRevenue = revenueHistory
    .filter(r => isSameDay(new Date(r.date), today))
    .reduce((sum, r) => sum + getTotalRevenue(r, showExVat), 0);

  const thisWeekRevenue = revenueHistory
    .filter(r => {
      const d = new Date(r.date);
      return d >= weekStart && d <= today;
    })
    .reduce((sum, r) => sum + getTotalRevenue(r, showExVat), 0);

  const weekdayTotals = revenueHistory
    .filter(r => getISODay(new Date(r.date)) === isoToday)
    .map(r => getTotalRevenue(r, showExVat));
  const averageWeekdayRevenue = weekdayTotals.length
    ? weekdayTotals.reduce((a, b) => a + b, 0) / weekdayTotals.length
    : 0;

  const weekBuckets: Record<string, number> = {};
  revenueHistory.forEach(r => {
    const d = new Date(r.date);
    const wk = `${getYear(d)}-W${getISOWeek(d)}`;
    const dayNo = getISODay(d);
    if (dayNo <= isoToday) {
      weekBuckets[wk] = (weekBuckets[wk] || 0) + getTotalRevenue(r, showExVat);
    }
  });
  const weekVals = Object.values(weekBuckets);
  const averageWeekRevenue = weekVals.length
    ? weekVals.reduce((a, b) => a + b, 0) / weekVals.length
    : 0;

  const monthRevenue = revenueHistory
    .filter(r => isSameMonth(new Date(r.date), today))
    .reduce((sum, r) => sum + getTotalRevenue(r, showExVat), 0);

  const monthBuckets: Record<string, number> = {};
  revenueHistory.forEach(r => {
    const d = new Date(r.date);
    const key = `${getYear(d)}-${getMonth(d)}`;
    monthBuckets[key] = (monthBuckets[key] || 0) + getTotalRevenue(r, showExVat);
  });
  const monthVals = Object.values(monthBuckets);
  const averageMonthRevenue = monthVals.length
    ? monthVals.reduce((a, b) => a + b, 0) / monthVals.length
    : 0;

  const totalRevenue = revenueHistory.reduce((sum, r) => sum + getTotalRevenue(r, showExVat), 0);

  return {
    todaysRevenue,
    thisWeekRevenue,
    averageWeekdayRevenue,
    averageWeekRevenue,
    monthRevenue,
    averageMonthRevenue,
    totalRevenue,
  };
}

export function getTotalRevenue(revenueHistory: RevenueHistory, showExVat: boolean = true): number {
  return revenueHistory.revenueData.categories.reduce(
    (total, category) => total + (showExVat ? (category.exVat || 0) : (category.price || 0)),
    0
  );
}

export function getWeekRevenue(revenueHistory: RevenueHistory[], weekStart: Date): RevenueHistory[] {
  const weekEnd = endOfISOWeek(weekStart);
  return revenueHistory.filter(revenue => {
    const revenueDate = new Date(revenue.date);
    return revenueDate >= weekStart && revenueDate <= weekEnd;
  });
}

export function getWeekYear(date: Date): number {
  return getYear(date);
}

export function formatWeekRange(weekStart: Date): string {
  const weekEnd = endOfISOWeek(weekStart);
  const startFormat = format(weekStart, 'EEE d MMM');
  const endFormat = format(weekEnd, 'EEE d MMM yyyy');
  return `${startFormat} - ${endFormat}`;
}

export function getAvailableWeeks(revenueHistory: RevenueHistory[]): Date[] {
  if (revenueHistory.length === 0) return [];

  const weeks = new Set<string>();
  revenueHistory.forEach(revenue => {
    const weekStart = startOfISOWeek(new Date(revenue.date));
    weeks.add(weekStart.toISOString());
  });

  return Array.from(weeks)
    .map(dateStr => new Date(dateStr))
    .sort((a, b) => a.getTime() - b.getTime());
}

export async function updateRevenueItemQuantity(
  restaurantRevenueSourceId: string,
  itemName: string,
  newQuantity: number,
  startDate: Date,
  endDate: Date
): Promise<{ success: boolean; updateCount: number; message: string }> {
  const token = await getAccessToken();
  const res = await fetch(`/api/revenuehistory/updateRevenueItemQuantity`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      restaurantRevenueSourceId,
      itemName,
      newQuantity,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    }),
  });

  if (!res.ok) {
    throw new Error(`Failed to update revenue item quantity: ${res.status} ${res.statusText}`);
  }

  return res.json();
}